const STEP_KEYS = ['core', 'specs', 'assets', 'accessories', 'review'] as const
<template>
  <div v-if="open" class="fixed inset-0 z-[90] flex items-center justify-center px-4 sm:px-8 py-10">
    <div class="onboarding-backdrop" @click="onClose" />
    <div class="onboarding-shell">
      <header class="onboarding-header">
        <div>
          <p class="onboarding-eyebrow">Add Equipment Type</p>
          <h3 class="onboarding-title">{{ STEPS[currentStep].title }}</h3>
          <p class="onboarding-subtitle">{{ STEPS[currentStep].subtitle }}</p>
        </div>
        <button type="button" class="onboarding-close" @click="onClose">
          <span>Close</span>
          <svg class="onboarding-close-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6" d="M6 6l8 8M14 6l-8 8" />
          </svg>
        </button>
      </header>

      <nav class="onboarding-steps">
        <button
          v-for="(step, index) in STEPS"
          :key="step.key"
          class="step-chip"
          :class="stepChipClass(index)"
          @click="goToStep(index)"
          type="button"
        >
          <span class="step-index">{{ index + 1 }}</span>
          <span class="step-label">{{ step.label }}</span>
        </button>
      </nav>

      <section class="onboarding-body custom-scroll">
        <component
          :is="STEPS[currentStep].component"
          :model-value="form"
          @update:model-value="updateForm"
          :inventory-types="inventoryTypes"
          :products="availableProducts"
          :accessory-options="accessoryOptions"
          :auto-name="autoName"
          :manual-name-edited="manualNameEdited"
          :selected-accessories="selectedAccessoryIds"
          @upload-request="takeUploadRequest"
          @remove-asset="handleRemoveAsset"
          @manual-name="onManualName"
          @update:manualNameEdited="updateManualNameEdited"
          @accessories-selected="setSelectedAccessories"
        />
      </section>

      <footer class="onboarding-footer">
        <div class="footer-left" v-if="currentStep > 0">
          <button type="button" class="btn-secondary" @click="previousStep" :disabled="saving">
            Back
          </button>
        </div>
        <div class="footer-left" v-else></div>

        <div class="footer-right">
          <span v-if="submitError" class="text-rose-400 text-xs mr-4">{{ submitError }}</span>
          <button
            v-if="props.enableDrafts && props.mode !== 'edit'"
            type="button"
            class="btn-secondary"
            @click="saveDraft"
            :disabled="saving || savingDraft || props.loading"
          >
            {{ savingDraft || props.loading ? 'Saving…' : 'Save Draft' }}
          </button>
          <button type="button" class="btn-secondary" @click="onClose" :disabled="saving">
            Cancel
          </button>
          <button
            type="button"
            class="btn-primary"
            :disabled="saving || props.loading || !canAdvance"
            @click="nextOrSubmit"
          >
            {{
          saving || props.loading
            ? 'Saving…'
            : currentStep === STEPS.length - 1
              ? props.mode === 'edit'
                ? 'Save Changes'
                : 'Create Equipment Type'
              : 'Next'
            }}
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch, defineAsyncComponent, onMounted, onBeforeUnmount } from 'vue'
import type {
  EquipmentTypeSummary,
  EquipmentTypeCreateInput,
  EquipmentTypeSpecs,
  EquipmentTypeAsset,
  EquipmentTypeAccessory,
  EquipmentProduct,
  EquipmentTypeStatus,
  EquipmentTypeDraft,
} from '@/types/inventory'
import { useInventoryStore } from '@/stores/inventory'
import { uploadEquipmentAsset, deleteEquipmentAsset } from '@/services/inventoryStorage'
import { createLogger } from '@/lib/logger'

const log = createLogger('Onboarding')

const props = defineProps<{
  open: boolean
  inventoryTypes: EquipmentTypeSummary[]
  products?: EquipmentProduct[]
  loading?: boolean
  draftId?: number | null
  mode?: 'create' | 'edit'
  initialData?: EquipmentTypeCreateInput | null
  typeId?: number | null
  enableDrafts?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'submit', payload: EquipmentTypeCreateInput): void
  (e: 'save-draft', payload: EquipmentTypeCreateInput): void
  (e: 'remove-asset', payload: EquipmentTypeAsset): void
}>()

const form = reactive<
  EquipmentTypeCreateInput & {
    specs: EquipmentTypeSpecs
    assets: EquipmentTypeAsset[]
    accessories: EquipmentTypeAccessory[]
  }
>({
  name: '',
  make: '',
  model: '',
  descriptor: '',
  categoryId: undefined,
  isAccessory: false,
  products: ['POTS Repl'],
  specs: {
    shippingWeightOz: 18,
    shippingHeightIn: 4.5,
    shippingWidthIn: 9.8,
    shippingDepthIn: 6,
    metadata: {},
  },
  assets: [],
  accessories: [],
})

const errors = reactive<Record<string, string[]>>({})
const currentStep = ref(0)
watch(currentStep, (value) => {
  inventoryStore.setOnboardingStep(STEP_KEYS[value])
})
const saving = ref(false)
const savingDraft = ref(false)
const submitError = ref<string | null>(null)
const activeDraftId = ref<number | null>(null)

const inventoryStore = useInventoryStore()
const STEP_KEYS = ['core', 'specs', 'assets', 'accessories', 'review'] as const
const onboardingKey = computed(() => inventoryStore.onboardingStepKey)
const availableProducts = computed(() => props.products ?? inventoryStore.products)
const accessoryOptions = computed(() => props.inventoryTypes.filter((type) => type.isAccessory))
const selectedAccessoryIds = ref<number[]>([])
const draftLoaded = ref(false)
const autosaveTimer = ref<number | null>(null)
const canAdvance = computed(() => {
  if (currentStep.value === 0) {
    return Boolean(form.make.trim()) && Boolean(form.model.trim())
  }
  if (currentStep.value === 1) {
    return (
      form.specs.shippingWeightOz != null &&
      form.specs.shippingHeightIn != null &&
      form.specs.shippingWidthIn != null &&
      form.specs.shippingDepthIn != null
    )
  }
  return true
})

const autoName = computed(() => {
  const make = form.make.trim()
  const model = form.model.trim()
  if (!make && !model) return ''
  return [make, model].filter(Boolean).join(' ')
})

const manualNameEdited = ref(false)

const STEPS = [
  {
    key: 'core',
    label: 'Core Details',
    title: 'New Equipment Onboarding',
    subtitle: 'Name, make, model, descriptor, products.',
    component: defineAsyncComponent(() => import('./onboarding/StepCoreDetails.vue')),
  },
  {
    key: 'specs',
    label: 'Logistics',
    title: 'Capture logistics data',
    subtitle: 'Weight and dimensions for shipping labels.',
    component: defineAsyncComponent(() => import('./onboarding/StepLogistics.vue')),
  },
  {
    key: 'assets',
    label: 'Assets',
    title: 'Upload imagery and datasheets',
    subtitle: 'Front/back photos and documentation.',
    component: defineAsyncComponent(() => import('./onboarding/StepAssets.vue')),
  },
  {
    key: 'accessories',
    label: 'Accessories',
    title: 'Related equipment',
    subtitle: 'Link accessories or compatible models.',
    component: defineAsyncComponent(() => import('./onboarding/StepAccessories.vue')),
  },
  {
    key: 'review',
    label: 'Review',
    title: 'Review & confirm',
    subtitle: 'Double-check before creating.',
    component: defineAsyncComponent(() => import('./onboarding/StepReview.vue')),
  },
]

watch(
  () => props.open,
  (value) => {
    if (value) {
      resetForm()
      inventoryStore.setOnboardingStep('core')
      if (props.initialData) {
        hydrateFromPayload(props.initialData)
      }
      if (props.mode === 'edit') {
        currentStep.value = STEPS.length - 1
      }
    }
  },
)

watch(
  () => props.initialData,
  (value) => {
    if (props.open && value) {
      hydrateFromPayload(value)
    }
  },
  { immediate: true },
)

watch(
  () => [form.make, form.model],
  () => {
    if (!manualNameEdited.value) {
      form.name = autoName.value.trim()
    }
  },
)

watch(() => props.loading, (value) => {
  if (value === false) {
    saving.value = false
    savingDraft.value = false
    submitError.value = null
  }
})

onMounted(async () => {
  if (!inventoryStore.products.length && !inventoryStore.productsLoading) {
    await inventoryStore.loadProducts()
  }
  if (!inventoryStore.drafts.length && !inventoryStore.draftsLoading) {
    await inventoryStore.loadDrafts()
  }
  if (props.draftId && !draftLoaded.value) {
    hydrateFromDraft(props.draftId)
  }
})

watch(
  () => props.draftId,
  (id) => {
    if (id) {
      hydrateFromDraft(id)
    }
  },
)

function resetForm() {
  form.name = ''
  form.make = ''
  form.model = ''
  form.descriptor = ''
  form.categoryId = undefined
  form.isAccessory = false
  form.products = ['POTS Repl']
  form.specs = {
    shippingWeightOz: 18,
    shippingHeightIn: 4.5,
    shippingWidthIn: 9.8,
    shippingDepthIn: 6,
    metadata: {},
  }
  form.assets = []
  form.accessories = []
  Object.keys(errors).forEach((key) => (errors[key] = []))
  currentStep.value = 0
  saving.value = false
  savingDraft.value = false
  manualNameEdited.value = false
  selectedAccessoryIds.value = []
  draftLoaded.value = false
  activeDraftId.value = null
  if (autosaveTimer.value) {
    window.clearTimeout(autosaveTimer.value)
    autosaveTimer.value = null
  }
}

onBeforeUnmount(() => {
  if (autosaveTimer.value) {
    window.clearTimeout(autosaveTimer.value)
    autosaveTimer.value = null
  }
})

function onClose() {
  if (saving.value) return
  resetForm()
  inventoryStore.setOnboardingStep('core')
  emit('close')
}

function goToStep(index: number) {
  if (index < currentStep.value && !saving.value) {
    currentStep.value = index
  }
}

function previousStep() {
  if (currentStep.value > 0) {
    currentStep.value -= 1
  }
}

function nextOrSubmit() {
  if (saving.value) return
  if (currentStep.value < STEPS.length - 1) {
    currentStep.value += 1
    return
  }
  submit()
}

function submit() {
  if (saving.value || props.loading) return
  submitError.value = null
  saving.value = true
  try {
    emit('submit', payloadForEmit('active'))
  } catch (error) {
    saving.value = false
    submitError.value = error instanceof Error ? error.message : String(error)
  }
}

async function takeUploadRequest(payload: { type: EquipmentTypeAsset['assetType']; file?: File }) {
  const file = payload.file ?? (await promptForFile(payload.type))
  if (!file) return
  const tempId = `${payload.type}-${Date.now()}-${Math.random().toString(16).slice(2)}`
  const previewUrl = await readAsDataUrl(file)
  const assetRecord: EquipmentTypeAsset = {
    tempId,
    assetType: payload.type,
    filePath: null,
    previewUrl,
    localFile: file,
    isPending: true,
  }
  form.assets = [...form.assets.filter((asset) => asset.assetType !== payload.type), assetRecord]
  try {
    const uploadResult = await uploadFile(payload.type, file)
    const asset = form.assets.find((item) => item.tempId === tempId)
    if (asset) {
      asset.filePath = uploadResult.publicUrl || uploadResult.path
      asset.previewUrl = uploadResult.publicUrl || previewUrl
      asset.metadata = { storagePath: uploadResult.path }
      asset.isPending = false
      delete asset.localFile
      delete asset.tempId
    }
  } catch (error) {
    form.assets = form.assets.filter((item) => item.tempId !== tempId)
    submitError.value = error instanceof Error ? error.message : String(error)
    throw error
  }
}

async function uploadFile(type: EquipmentTypeAsset['assetType'], file: File) {
  const typeKey = autoName.value || form.name || `type-${Date.now()}`
  const result = await uploadEquipmentAsset({
    typeKey,
    assetType: type,
    file,
  })
  if (!result.path || !result.publicUrl) {
    throw new Error('Upload did not return a storage path')
  }
  return result
}

async function removeUploadedAsset(asset: EquipmentTypeAsset) {
  const storagePath = asset.metadata?.storagePath
  if (typeof storagePath !== 'string' || !storagePath.trim()) return
  try {
    await deleteEquipmentAsset(storagePath)
  } catch (error) {
    log.error('Failed to delete asset from storage', error)
  }
}

async function handleRemoveAsset(target: EquipmentTypeAsset) {
  if (!target) return
  await removeUploadedAsset(target)
  form.assets = form.assets.filter((asset) => asset !== target)
  emit('remove-asset', target)
}

function promptForFile(type: EquipmentTypeAsset['assetType']): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = type === 'datasheet' ? '.pdf,.png,.jpg,.jpeg' : 'image/*'
    input.onchange = () => resolve(input.files?.[0] ?? null)
    input.click()
  })
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

async function saveDraft() {
  if (saving.value || savingDraft.value || props.loading) return
  submitError.value = null
  savingDraft.value = true
  try {
    const draft = await emitPromise<{ id: number }>('save-draft', payloadForEmit('draft'))
    if (draft?.id) {
      activeDraftId.value = draft.id
    }
    savingDraft.value = false
  } catch (error) {
    savingDraft.value = false
    submitError.value = error instanceof Error ? error.message : String(error)
  }
}

function payloadForEmit(status: EquipmentTypeStatus): EquipmentTypeCreateInput {
  return {
    status,
    name: form.name,
    make: form.make,
    model: form.model,
    descriptor: form.descriptor,
    categoryId: form.categoryId,
    isAccessory: form.isAccessory,
    products: form.products,
    specs: form.specs,
    assets: form.assets,
    accessories: selectedAccessoryIds.value.map((id) => ({ accessoryForTypeId: id })),
  }
}

function hydrateFromDraft(id: number) {
  const draft = inventoryStore.drafts.find((item) => item.id === id)
  if (!draft) {
    log.warn('Draft not found for id', id)
    return
  }
  draftLoaded.value = true
  activeDraftId.value = draft.id
  hydrateFromPayload({
    status: 'draft',
    name: draft.name ?? '',
    make: draft.make ?? '',
    model: draft.model ?? '',
    descriptor: draft.descriptor ?? undefined,
    categoryId: draft.category_id ?? undefined,
    isAccessory: draft.is_accessory ?? false,
    products: draft.products ?? ['POTS Repl'],
    specs:
      draft.specs ?? {
        shippingWeightOz: 18,
        shippingHeightIn: 4.5,
        shippingWidthIn: 9.8,
        shippingDepthIn: 6,
        metadata: {},
      },
    assets: draft.assets ?? [],
    accessories: draft.accessories ?? [],
  })
  selectedAccessoryIds.value = (draft.accessories ?? [])
    .map((item) => item.accessoryForTypeId)
    .filter((val): val is number => typeof val === 'number')
  const stepIndex = STEPS.findIndex((step) => step.key === (draft.progress_step ?? 'core'))
  if (stepIndex >= 0) {
    currentStep.value = stepIndex
  }
  draftLoaded.value = true
  manualNameEdited.value = Boolean(form.name && form.name !== autoName.value)
}

function hydrateFromPayload(payload: EquipmentTypeCreateInput) {
  form.name = payload.name
  form.make = payload.make
  form.model = payload.model
  form.descriptor = payload.descriptor ?? ''
  form.categoryId = payload.categoryId ?? undefined
  form.isAccessory = payload.isAccessory ?? false
  form.products = payload.products ?? ['POTS Repl']
  form.specs =
    payload.specs ?? {
      shippingWeightOz: 18,
      shippingHeightIn: 4.5,
      shippingWidthIn: 9.8,
      shippingDepthIn: 6,
      metadata: {},
    }
  form.assets = payload.assets ?? []
  form.accessories = payload.accessories ?? []
  selectedAccessoryIds.value = (payload.accessories ?? [])
    .map((item) => item.accessoryForTypeId)
    .filter((val): val is number => typeof val === 'number')
  manualNameEdited.value = Boolean(form.name && form.name !== autoName.value)
}

function onManualName() {
  manualNameEdited.value = true
}

function updateManualNameEdited(value: boolean) {
  manualNameEdited.value = value
}

function setSelectedAccessories(ids: number[]) {
  selectedAccessoryIds.value = ids
}

function updateForm(value: EquipmentTypeCreateInput) {
  Object.assign(form, value)
}

function stepChipClass(index: number) {
  if (index === currentStep.value) return 'is-active'
  if (index < currentStep.value) return 'is-complete'
  return ''
}

function markErrors(stepKey: string, messages: string[]) {
  errors[stepKey] = messages
}

function emitPromise<T>(event: string, payload: unknown): Promise<T> {
  return new Promise((resolve, reject) => {
    try {
      const maybe = (emit as any)(event, payload)
      if (maybe && typeof maybe.then === 'function') {
        maybe.then(resolve).catch(reject)
      } else {
        resolve(maybe as T)
      }
    } catch (error) {
      reject(error)
    }
  }) as Promise<T>
}

defineExpose({ form, errors, savingState: saving, markErrors })
</script>

<style scoped>
.onboarding-backdrop {
  position: fixed;
  inset: 0;
  background: linear-gradient(180deg, rgba(3, 7, 18, 0.88), rgba(3, 7, 18, 0.75));
  backdrop-filter: blur(28px);
}

.onboarding-shell {
  position: relative;
  width: clamp(720px, 70vw, 980px);
  height: clamp(560px, 72vh, 700px);
  max-height: 82vh;
  background: linear-gradient(155deg, rgba(5, 8, 14, 0.98) 0%, rgba(2, 4, 8, 0.99) 100%);
  border: 1px solid rgba(30, 41, 59, 0.48);
  border-radius: 24px;
  box-shadow:
    0 36px 96px rgba(9, 13, 26, 0.72),
    0 24px 58px rgba(12, 19, 33, 0.58),
    0 0 42px rgba(15, 23, 42, 0.36),
    0 0 0 1px rgba(30, 41, 59, 0.5);
  display: grid;
  grid-template-rows: auto auto 1fr auto;
  overflow: hidden;
  backdrop-filter: blur(18px) saturate(118%);
}

.onboarding-header {
  padding: 1.75rem 2.25rem 1.5rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid rgba(30, 41, 59, 0.6);
}


.onboarding-eyebrow {
  font-size: 0.65rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: rgba(203, 213, 225, 0.5);
  margin-bottom: 0.5rem;
}

.onboarding-title {
  color: #f8fafc;
  font-size: 1.5rem;
  margin: 0;
}

.onboarding-subtitle {
  color: rgba(148, 163, 184, 0.8);
  font-size: 0.9rem;
  margin-top: 0.35rem;
}

.onboarding-close {
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(37, 99, 235, 0.28);
  color: rgba(226, 232, 240, 0.86);
  font-size: 0.8rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 0.55rem 0.95rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 999px;
  transition: all 0.22s ease;
}

.onboarding-close:hover {
  color: #f8fafc;
  border-color: rgba(37, 99, 235, 0.42);
  background: rgba(37, 99, 235, 0.18);
}

.onboarding-close-icon {
  width: 1rem;
  height: 1rem;
}

.onboarding-steps {
  padding: 0.85rem 2.25rem 1rem;
  display: flex;
  gap: 0.6rem;
  overflow-x: auto;
}


.step-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 999px;
  border: 1px solid rgba(30, 41, 59, 0.7);
  background: rgba(15, 23, 42, 0.9);
  color: rgba(226, 232, 240, 0.78);
  padding: 0.35rem 0.75rem;
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  transition:
    border 0.2s ease,
    background 0.2s ease;
}

.step-chip.is-active {
  border-color: rgba(37, 99, 235, 0.55);
  background: rgba(37, 99, 235, 0.14);
}

.step-chip.is-complete {
  border-color: rgba(94, 234, 212, 0.45);
  background: rgba(45, 212, 191, 0.14);
}

.step-index {
  font-weight: 600;
}

.step-label {
  font-size: 0.7rem;
}

.onboarding-body {
  padding: 1.4rem 2.25rem 1.25rem;
  overflow: auto;
}

.onboarding-footer {
  padding: 1.4rem 2.25rem 1.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid rgba(30, 41, 59, 0.6);
}

.footer-right {
  display: flex;
  gap: 0.75rem;
}

.footer-right .btn-secondary,
.footer-right .btn-primary {
  padding: 0.58rem 1.15rem;
  border-radius: 0.65rem;
  font-size: 0.85rem;
  letter-spacing: 0.02em;
  border: 1px solid rgba(30, 41, 59, 0.7);
  background: rgba(15, 23, 42, 0.78);
  color: rgba(226, 232, 240, 0.9);
  transition: all 0.22s ease;
}

.footer-right .btn-secondary:hover:not(:disabled) {
  border-color: rgba(37, 99, 235, 0.32);
  background: rgba(37, 99, 235, 0.18);
}

.footer-right .btn-primary {
  border-color: rgba(37, 99, 235, 0.6);
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.32), rgba(15, 23, 42, 0.92));
  box-shadow: 0 10px 24px rgba(37, 99, 235, 0.22);
  color: rgba(240, 249, 255, 0.96);
}

.footer-right .btn-primary:hover:not(:disabled) {
  filter: brightness(1.05);
  box-shadow: 0 12px 28px rgba(37, 99, 235, 0.3);
}

.footer-right button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  box-shadow: none;
}

.custom-scroll {
  scrollbar-width: thin;
}
</style>
